import React from 'react'
import classNames from 'classnames'
import { DateFormat, moment } from '@pbt/pbt-ui-components'

import type { PrescriptionTimelineItem } from '~/types'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import RxCardHeader from '../RxCardHeader'
import PrescriptionsCardDetails from './PrescriptionsCardDetails'
import {
  ReviewApproveButton,
  useShowReviewApproveButton,
} from './rxTracker/ReviewApproveButton'
import { RxCardDetails } from './rxTracker/RxCardDetails'
import {
  computeRelativeTime,
  getPrescriptionWithOrigin,
} from './rxTracker/rxCardUtilities'
import { useRxCardContent } from './rxTracker/useRxCardContent'

import styles from './RxPrescriptionsCard.module.scss'

interface RxPrescriptionsCardProps {
  actionNeeded?: boolean
  icon?: React.JSXElementConstructor<any>
  id?: string
  index: number
  isInvoice?: boolean
  item: PrescriptionTimelineItem
  onClick?: () => void
  titlePinColor?: string | null
}

const RxPrescriptionsCard = ({
  item: itemProp,
  isInvoice,
  index,
  actionNeeded,
  icon: IconComponent,
  id,
  onClick,
  titlePinColor,
}: RxPrescriptionsCardProps) => {
  const timezone = useCurrentBusinessTimezone()

  const item = getPrescriptionWithOrigin(itemProp)

  const itemDate =
    item.date ??
    item.prescription?.updatedAt ??
    item.prescription?.creationDate ??
    ''

  const subheader = computeRelativeTime(itemDate, timezone)
  const dateTitle = itemDate
    ? moment.tz(itemDate, timezone).format(DateFormat.FULL_DATE_COMMA_PIPE_TIME)
    : ''

  const rxContent = useRxCardContent(item.prescription, {
    itemDate,
  })
  const { prescriptionStateName } = rxContent

  const shouldShowApproveButton = useShowReviewApproveButton(
    item.prescription,
    prescriptionStateName ?? null,
  )

  return (
    <div className={styles.root} id={id}>
      {IconComponent && (
        <div className={styles.iconContainer}>
          <div
            className={classNames(styles.iconPaper, {
              [styles.actionNeededIconContainer]: actionNeeded,
            })}
          >
            <IconComponent
              className={classNames(styles.icon, {
                [styles.actionNeededIcon]: actionNeeded,
              })}
            />
          </div>
        </div>
      )}
      {IconComponent && (
        <div className={styles.iconContainerMobile}>
          <div
            className={classNames(styles.iconPaperMobile, {
              [styles.actionNeededIconContainer]: actionNeeded,
            })}
          >
            <IconComponent
              className={classNames(styles.icon, {
                [styles.actionNeededIcon]: actionNeeded,
              })}
            />
          </div>
        </div>
      )}
      <div className={styles.container}>
        <div
          className={classNames(styles.mainPaper, {
            [styles.actionNeededContainer]: actionNeeded,
          })}
        >
          <div className={styles.rxCardContent}>
            <RxCardHeader
              actionNeeded={actionNeeded}
              dateTitle={dateTitle}
              subheader={subheader}
              timelineItem={item}
              titlePinColor={titlePinColor}
              onClick={onClick}
            />
            <RxCardDetails index={index} isInvoice={isInvoice} item={item} />
            {shouldShowApproveButton && (
              <PrescriptionsCardDetails
                hideDivider
                approvalAction={
                  <ReviewApproveButton
                    prescription={item.prescription}
                    prescriptionStateName={prescriptionStateName ?? ''}
                  />
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RxPrescriptionsCard
